import {useCallback} from 'react';
import {Alert, Snackbar} from '@mui/material';
import {useRecoilState} from 'recoil';
import {snackbarState} from '../recoil/snackBar/snackBarAtom';
import {
  ErrorOutline,
  InfoOutlined,
  TaskAlt,
  WarningAmber,
} from '@mui/icons-material';

const icons = {
  success: <TaskAlt />,
  error: <ErrorOutline />,
  warning: <WarningAmber />,
  info: <InfoOutlined />,
};

// One snackbar exists app-wide, so one module-scope timer handle is enough;
// tracking it lets a forced message clear the previous message's hide timer
// instead of being closed early by it.
let hideTimer: ReturnType<typeof setTimeout> | undefined;

export default function useSnackbar() {
  const [snackbarOptions, setSnackbarOptions] = useRecoilState(snackbarState);

  const openSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'success',
    opts?: {duration?: number; force?: boolean},
  ) => {
    if (snackbarOptions.open && !opts?.force) {
      return;
    }
    const duration = opts?.duration ?? 3000;
    clearTimeout(hideTimer);
    setSnackbarOptions({open: true, message, severity, duration});

    hideTimer = setTimeout(() => {
      setSnackbarOptions({open: false, message: '', severity: 'success'});
    }, duration + 300);
  };

  const BidmiiSnackbar = useCallback(
    () => (
      <Snackbar
        open={snackbarOptions.open}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        autoHideDuration={snackbarOptions.duration ?? 3000}
        onClose={() =>
          setSnackbarOptions({open: false, message: '', severity: 'success'})
        }>
        <Alert
          severity={snackbarOptions.severity}
          variant="filled"
          icon={icons[snackbarOptions.severity]}
          sx={{
            // width: "100%",
            '&.MuiAlert-colorSuccess': {bgcolor: `forest.main`},
            '&.MuiAlert-colorError': {bgcolor: `brick.main`},
            '&.MuiAlert-colorWarning': {bgcolor: `gold.main`},
            '&.MuiAlert-colorInfo': {bgcolor: `eggplant.main`},
          }}>
          {snackbarOptions.message}
        </Alert>
      </Snackbar>
    ),
    [snackbarOptions, setSnackbarOptions],
  );

  return {openSnackbar, BidmiiSnackbar};
}
